/*
 *  This document is the CONFIDENTIAL PROPERTY of Corero Network Security, Inc.
 *  Copyright (C) 2024-2026 by Corero Network Security, Inc. All rights reserved.
 *  Notice of copyright on this document does not constitute publication.
 */

import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';
import 'alertifyjs/build/css/themes/bootstrap.css';
import {escapeHtml} from '../utils/commonUtils';

const applyTemplate = (template, args) => {
    if (!args || args.length === 0) return template;
    return template.replace(/\{(\d+)\}/g, (_, i) => escapeHtml(args[parseInt(i, 10)]));
};

/**
 * Basic notification service.
 *
 * Methods accept an optional list of template arguments after the fixed
 * parameters. Use {0}, {1}, … placeholders in the message string for
 * user-controlled values — they are HTML-escaped before substitution.
 * Hard-coded HTML (e.g. <br/>, <p class="...">) can be used freely in
 * the message string itself since it is authored by developers, not users.
 *
 * Example:
 *   Notify.confirm("Delete '{0}'?", okFn, row.name)
 *   Notify.info("Imported {0} successfully", fileName)
 */
class Notify {
    constructor() {
        alertify.set('notifier','delay', 10);
        alertify.defaults.glossary.title = '';
        alertify.defaults.moveable = false;
        alertify.defaults.theme.ok = "btn-blue btn btn-primary";
        alertify.defaults.theme.cancel = "btn-simple btn btn-secondary";
    }

    info(msg, ...args) {
        alertify.success(applyTemplate(msg, args));
    }

    warn(msg, ...args) {
        alertify.warning(applyTemplate(msg, args));
    }

    error(msg, ...args) {
        alertify.error(applyTemplate(msg, args));
    }

    confirm(msg, okFn, cancelFnOrFirstArg, ...rest) {
        let cancelFn = null;
        let args = [];
        if (typeof cancelFnOrFirstArg === 'function') {
            cancelFn = cancelFnOrFirstArg;
            args = rest;
        } else if (cancelFnOrFirstArg !== undefined) {
            args = [cancelFnOrFirstArg, ...rest];
        }
        alertify.confirm(applyTemplate(msg, args), okFn, cancelFn).set('reverseButtons', true).set('moveable', false);
    }

    dismiss() {
        alertify.dismissAll();
    }

}

const notify = new Notify();

export default notify;
